import { deleteLocalDocument, listSyncOutbox, markDocumentSynced, queueDocumentForSync, saveLocal, type SyncOutboxRecord } from '../persistence/localRepository';
import { SyncGateway, SyncGatewayError } from './SyncGateway';
import type { DiagramDocument } from '../../core/types';

export interface SyncFlushResult {
  attempted: number;
  synced: number;
  conflicts: number;
  failed: number;
  conflictDocumentIds: string[];
  failedDocumentIds: string[];
  syncedDocuments: Array<{ document: DiagramDocument; revision: string }>;
  rekeyedDocuments: Array<{ fromId: string; document: DiagramDocument; revision: string }>;
  failedErrors: Array<{ documentId: string; message: string }>;
}

export interface SyncRecordResult {
  sourceDocumentId: string;
  document: DiagramDocument;
  revision: string;
  rekeyed: boolean;
}

function freshProjectIdentity(document: DiagramDocument): DiagramDocument {
  return {
    ...structuredClone(document),
    id: crypto.randomUUID(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Envia um registro da outbox e trata um caso específico de legado: um documento que o
 * navegador considera novo/local usa um ID que já está ocupado no servidor por um projeto
 * ao qual a sessão atual não tem acesso. O servidor responde 404 para não vazar a existência
 * desse projeto. Como o envio estava explicitamente em modo de criação (baseRevision=null),
 * criamos uma identidade nova para a cópia local e tentamos de novo sem tocar no projeto remoto.
 */
export async function pushSyncRecordWithRecovery(
  gateway: SyncGateway,
  record: SyncOutboxRecord,
  ownerScope?: string | null,
  signal?: AbortSignal,
): Promise<SyncRecordResult> {
  try {
    const pushed = await gateway.push({ documentId: record.documentId, baseRevision: record.baseRevision, localRevision: record.localRevision, document: record.document }, signal);
    const acceptedDocument = pushed.document ?? record.document;
    await markDocumentSynced(record.documentId, record.localRevision, pushed.acceptedRevision, ownerScope);
    return { sourceDocumentId: record.documentId, document: acceptedDocument, revision: pushed.acceptedRevision, rekeyed: false };
  } catch (error) {
    const identityCollision = error instanceof SyncGatewayError && error.status === 404 && record.baseRevision === null;
    if (!identityCollision) throw error;

    const recovered = freshProjectIdentity(record.document);
    await saveLocal(recovered, ownerScope);
    await queueDocumentForSync(recovered, ownerScope, { force: true, baseRevision: null });
    try {
      const pushed = await gateway.push({ documentId: recovered.id, baseRevision: null, localRevision: recovered.updatedAt, document: recovered }, signal);
      const acceptedDocument = pushed.document ?? recovered;
      await markDocumentSynced(recovered.id, recovered.updatedAt, pushed.acceptedRevision, ownerScope);
      // Só removemos a identidade antiga depois de a nova cópia existir com confirmação do servidor.
      await deleteLocalDocument(record.documentId, ownerScope);
      return { sourceDocumentId: record.documentId, document: acceptedDocument, revision: pushed.acceptedRevision, rekeyed: true };
    } catch (retryError) {
      // O original continua íntegro. A identidade de recuperação incompleta não deve gerar um segundo card fantasma.
      await deleteLocalDocument(recovered.id, ownerScope);
      throw retryError;
    }
  }
}

/** Executa uma passada limitada da outbox. Só deve ser chamado quando o backend OWDEX estiver explicitamente habilitado. */
export async function flushSyncOutbox(gateway: SyncGateway, signal?: AbortSignal, maxItems = 10, ownerScope?: string | null): Promise<SyncFlushResult> {
  const records = (await listSyncOutbox(ownerScope)).slice(0, Math.max(1, Math.min(50, maxItems)));
  const result: SyncFlushResult = { attempted: 0, synced: 0, conflicts: 0, failed: 0, conflictDocumentIds: [], failedDocumentIds: [], syncedDocuments: [], rekeyedDocuments: [], failedErrors: [] };
  for (const record of records) {
    if (signal?.aborted) break;
    result.attempted += 1;
    try {
      const pushed = await pushSyncRecordWithRecovery(gateway, record, ownerScope, signal);
      result.synced += 1;
      result.syncedDocuments.push({ document: pushed.document, revision: pushed.revision });
      if (pushed.rekeyed) result.rekeyedDocuments.push({ fromId: pushed.sourceDocumentId, document: pushed.document, revision: pushed.revision });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('Conflito de versão')) { result.conflicts += 1; result.conflictDocumentIds.push(record.documentId); }
      else { result.failed += 1; result.failedDocumentIds.push(record.documentId); result.failedErrors.push({ documentId: record.documentId, message: message || 'Falha desconhecida de sincronização.' }); }
    }
  }
  return result;
}
